import { Filter } from "@app/analytics/meetops/types";
import { User } from "@app/common/types";

export type FilterMeta = TextFilterMeta | NumberFilterMeta | ChoiceFilterMeta;

export interface Operator {
  id: string;
  displayName: string;
  queryName: string;
  longName?: string;
}

export type FilterFunc<T> = (args: T) => Filter;

const WAS_IN_MEETING: Filter = {
  name: "p_time_in_meeting",
  operator: "not_equal",
  comparisonValue: null,
};

const MISSED_MEETING: Filter = {
  name: "p_time_in_meeting",
  operator: "equal",
  comparisonValue: null,
};

const LATE_TO_MEETING: Filter = {
  name: "p_on_time",
  operator: "equal",
  comparisonValue: "False",
};

const MULTI_PARTICIPANT_MEETING: Filter = {
  name: "s_observed_participant_count",
  operator: "greater_than",
  comparisonValue: 1,
};

const IS_RECURRING_MEETING: Filter = {
  name: "recurring_event_id",
  operator: "not_equal",
  comparisonValue: null,
};

export const wasInMeeting: FilterFunc<void> = () => WAS_IN_MEETING;
export const missedMeeting: FilterFunc<void> = () => MISSED_MEETING;
export const lateToMeeting: FilterFunc<void> = () => LATE_TO_MEETING;
export const multiParticipantMeeting: FilterFunc<void> = () =>
  MULTI_PARTICIPANT_MEETING;
export const isRecurringMeeting: FilterFunc<void> = () => IS_RECURRING_MEETING;
export const isInSeries: FilterFunc<string[]> = (seriesIds: string[]) => ({
  name: "recurring_event_id",
  operator: "in",
  comparisonValue: seriesIds,
});

export const isUser: FilterFunc<User["id"]> = (userId: User["id"]) => ({
  name: "user_id",
  operator: "equal",
  comparisonValue: userId,
});

export const isNotUser: FilterFunc<User["id"]> = (userId: User["id"]) => ({
  name: "user_id",
  operator: "not_equal",
  comparisonValue: userId,
});

export const onlyUsers: FilterFunc<User["id"][]> = (userIds: User["id"][]) => ({
  name: "user_id",
  operator: "in",
  comparisonValue: userIds,
});

export const onlySessions: FilterFunc<string[]> = (sessionIds) => ({
  name: "session_id",
  operator: "in",
  comparisonValue: sessionIds,
});

export const notNullTeams: FilterFunc<void> = () => ({
  name: "team_id",
  operator: "not_null",
  comparisonValue: null,
});

export const scheduledMeetings: FilterFunc<void> = () => ({
  name: "s_scheduled_duration",
  operator: "not_null",
  comparisonValue: null,
});

export const isParticipantName: FilterFunc<string> = (participantName) => ({
  name: "participant_name",
  operator: "equal",
  comparisonValue: participantName,
});

const TEXT_OPERATORS: Operator[] = [
  {
    id: "contains",
    queryName: "contains",
    displayName: "contains",
  },
  {
    id: "equals",
    queryName: "equal",
    displayName: "is exactly",
  },
  {
    id: "excludes",
    queryName: "regex_match",
    displayName: "excludes",
  },
];

const CHOICE_OPERATORS: Operator[] = [
  {
    id: "is",
    queryName: "equal",
    displayName: "is",
  },
  {
    id: "isNot",
    queryName: "not_equal",
    displayName: "is not",
  },
];

const NUMBER_OPERATORS: Operator[] = [
  {
    id: "equals",
    displayName: "=",
    queryName: "equal",
    longName: "equal to",
  },
  {
    id: "gt",
    displayName: ">",
    queryName: "greater_than",
    longName: "greater than",
  },
  {
    id: "gte",
    displayName: "≥",
    queryName: "greater_than_equal_to",
    longName: "greater than or equal to",
  },
  {
    id: "lt",
    displayName: "<",
    queryName: "less_than",
    longName: "less than",
  },
  {
    id: "not_equal",
    displayName: "≠",
    queryName: "not_equal",
    longName: "not equal to",
  },
];

export interface TextFilterMeta {
  id: string;
  displayName: string;
  queryName: string;
  format: "text";
  operators: typeof TEXT_OPERATORS;
  getValues: () => { name: string; value: string | number }[];
}

interface NumberFilterMeta {
  id: string;
  displayName: string;
  queryName: string;
  format: "number";
  operators: typeof NUMBER_OPERATORS;
  getRange: () => { min: number; max: number };
  toQueryFilter: (builderFilter: any) => Filter;
}

interface ChoiceFilterMeta {
  id: string;
  displayName: string;
  format: "choice";
  operators: typeof CHOICE_OPERATORS;
  getValues: () => { name: string; value: string | number }[];
}

export const meetingName: TextFilterMeta = {
  id: "meetingName",
  displayName: "Meeting Name",
  queryName: "title",
  format: "text",
  operators: TEXT_OPERATORS,
  getValues: () => [
    { name: "this is a meeting", value: "asdf" },
    { name: "another meeting", value: "fdas" },
  ],
};

export const participantCount: NumberFilterMeta = {
  id: "participantCount",
  queryName: "s_sentiment_score",
  displayName: "Participant #",
  format: "number",
  operators: NUMBER_OPERATORS,
  getRange: () => ({ min: 0, max: 25 }),
  toQueryFilter: (builderFilter) => {
    return {
      name: "s_observed_participant_count",
      operator: builderFilter.condition.operator.queryName,
      comparisonValue: builderFilter.condition.value,
    };
  },
};

export const meetingDurationPlanned: NumberFilterMeta = {
  id: "meetingDurationPlanned",
  queryName: "s_scheduled_duration",
  displayName: "Meeting Duration (Mins Planned)",
  format: "number",
  operators: NUMBER_OPERATORS,
  getRange: () => ({ min: 0, max: 300 }),
  toQueryFilter: (builderFilter) => {
    return {
      name: "s_scheduled_duration",
      operator: builderFilter.condition.operator.queryName,
      comparisonValue: parseInt(builderFilter.condition.value) * 60,
    };
  },
};

export const meetingDurationActual: NumberFilterMeta = {
  id: "meetingDurationActual",
  queryName: "s_observed_duration",
  displayName: "Meeting Duration (Mins Actual)",
  format: "number",
  operators: NUMBER_OPERATORS,
  getRange: () => ({ min: 0, max: 300 }),
  toQueryFilter: (builderFilter) => {
    return {
      name: "s_observed_duration",
      operator: builderFilter.condition.operator.queryName,
      comparisonValue: parseInt(builderFilter.condition.value) * 60,
    };
  },
};

export const meetingScore: NumberFilterMeta = {
  id: "meetingScore",
  queryName: "s_meeting_score",
  displayName: "Read Score",
  format: "number",
  operators: NUMBER_OPERATORS,
  getRange: () => ({ min: 0, max: 300 }),
  toQueryFilter: (builderFilter) => {
    return {
      name: "s_meeting_score",
      operator: builderFilter.condition.operator.queryName,
      comparisonValue: builderFilter.condition.value,
    };
  },
};

export const sentiment: NumberFilterMeta = {
  id: "sentiment",
  queryName: "s_sentiment_score",
  displayName: "Sentiment",
  format: "number",
  operators: NUMBER_OPERATORS,
  getRange: () => ({ min: 0, max: 100 }),
  toQueryFilter: (builderFilter) => {
    return {
      name: "s_sentiment_score",
      operator: builderFilter.condition.operator.queryName,
      comparisonValue: builderFilter.condition.value,
    };
  },
};

export const engagement: NumberFilterMeta = {
  id: "engagement",
  queryName: "s_engagement_score",
  displayName: "Engagement",
  format: "number",
  operators: NUMBER_OPERATORS,
  getRange: () => ({ min: 0, max: 300 }),
  toQueryFilter: (builderFilter) => {
    return {
      name: "s_engagement_score",
      operator: builderFilter.condition.operator.queryName,
      comparisonValue: builderFilter.condition.value,
    };
  },
};

export const talkTime: NumberFilterMeta = {
  id: "talkTime",
  queryName: "p_talk_time_ratio",
  displayName: "Talk Time (%)",
  format: "number",
  operators: NUMBER_OPERATORS,
  getRange: () => ({ min: 0, max: 300 }),
  toQueryFilter: (builderFilter) => {
    return {
      name: "p_talk_time_ratio",
      operator: builderFilter.condition.operator.queryName,
      comparisonValue: parseFloat(builderFilter.condition.value) / 100,
    };
  },
};

export const bias: NumberFilterMeta = {
  id: "talkTime",
  queryName: "p_bias",
  displayName: "Participant Bias",
  format: "number",
  operators: NUMBER_OPERATORS,
  getRange: () => ({ min: 0, max: 300 }),
  toQueryFilter: (builderFilter) => {
    return {
      name: "p_bias",
      operator: builderFilter.condition.operator.queryName,
      comparisonValue: builderFilter.condition.value,
    };
  },
};

export const charisma: NumberFilterMeta = {
  id: "charisma",
  queryName: "p_charisma",
  displayName: "Participant Charisma",
  format: "number",
  operators: NUMBER_OPERATORS,
  getRange: () => ({ min: 0, max: 300 }),
  toQueryFilter: (builderFilter) => {
    return {
      name: "p_charisma",
      operator: builderFilter.condition.operator.queryName,
      comparisonValue: builderFilter.condition.value,
    };
  },
};
